import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import TopBar from "../components/TopBar";
import { ArrowRight, ArrowLeft, ClockCountdown, WarningCircle } from "@phosphor-icons/react";
import { MicCheck } from "../components/test/MicCheck";
import { ListeningPartStep } from "../components/test/ListeningPartStep";
import { OptionList } from "../components/test/Inputs";
import { PartStep } from "../components/test/PartStep";
import { SpeakingStep } from "../components/test/SpeakingStep";
import { EmailTask, ChoiceTask } from "../components/test/WritingSteps";

const SECTION_INFO = {
  grammar:    { label: "Grammar",    order: 1, of: 5 },
  vocabulary: { label: "Vocabulary", order: 2, of: 5 },
  reading:    { label: "Reading",    order: 3, of: 5 },
  listening:  { label: "Listening",  order: 4, of: 5 },
  writing:    { label: "Writing",    order: 5, of: 5 },
  speaking:   { label: "Speaking",   order: 5, of: 5 },
};

function buildSteps(qs) {
  const steps = [];
  qs.grammar.forEach((q) => steps.push({ kind: "mcq", section: "grammar", q }));
  qs.vocabulary.forEach((q) => steps.push({ kind: "mcq", section: "vocabulary", q }));
  qs.reading.forEach((part, pi) =>
    part.questions.forEach((q) => steps.push({ kind: "part", section: "reading", part, q, partNo: pi + 1 })),
  );
  qs.listening.forEach((part, pi) => steps.push({ kind: "listening-part", section: "listening", part, partNo: pi + 1 }));
  if (qs.speaking) steps.push({ kind: "speaking", section: "speaking" });
  if (qs.speaking_question) steps.push({ kind: "speaking-question", section: "speaking" });
  steps.push({ kind: "writing-email", section: "writing" });
  steps.push({ kind: "writing-choice", section: "writing" });
  return steps;
}

const PROGRESS_KEY = "vx-test-progress";

function loadProgress(userId) {
  try {
    const p = JSON.parse(localStorage.getItem(PROGRESS_KEY) || "null");
    if (p && p.userId === userId && Date.now() - p.startedAt < 2 * 24 * 3600 * 1000) return p;
  } catch { /* ignore corrupt progress */ }
  localStorage.removeItem(PROGRESS_KEY);
  return null;
}

export default function Test() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [qs, setQs] = useState(null);
  const [blocked, setBlocked] = useState("");
  const [started, setStarted] = useState(false);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [writing, setWriting] = useState({ email: "", choice: "article", extended: "" });
  const [plays, setPlays] = useState({});
  const [speech, setSpeech] = useState(null);
  const [speechQ, setSpeechQ] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const startRef = useRef(null);

  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    const saved = loadProgress(user?.id);
    if (saved) {
      setQs(saved.qs); setAnswers(saved.answers); setWriting(saved.writing); setPlays(saved.plays); setSpeech(saved.speech || null); setSpeechQ(saved.speechQ || null);
      setIdx(saved.idx); startRef.current = saved.startedAt;
      setElapsed(Math.floor((Date.now() - saved.startedAt) / 1000)); setStarted(true);
      toast("Welcome back — your answers were restored.");
      return;
    }
    api.post("/test/session").then((r) => setQs(r.data))
      .catch((e) => {
        const msg = formatApiErrorDetail(e.response?.data?.detail) || "Could not load test.";
        if (e.response?.status === 403 && /attempts/.test(msg)) setBlocked(msg);
        else toast.error(msg);
      });
  }, [user?.id]);

  // Save progress so a reload or failed submit never loses answers.
  useEffect(() => {
    if (!started || !qs) return;
    try {
      localStorage.setItem(PROGRESS_KEY, JSON.stringify({
        userId: user?.id, qs, answers, writing, plays, speech, speechQ, idx, startedAt: startRef.current,
      }));
    } catch { /* storage full: keep going without saving */ }
  }, [started, qs, answers, writing, plays, speech, speechQ, idx, user?.id]);

  useEffect(() => {
    if (!started) return;
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, [started]);

  const steps = useMemo(() => (qs ? buildSteps(qs) : []), [qs]);
  const current = steps[idx];
  const answered = Object.keys(answers).length;
  const totalMcq = steps.reduce((n, s) => n + (s.kind === "mcq" || s.kind === "part" ? 1 : s.kind === "listening-part" ? s.part.questions.length : 0), 0);
  const remaining = Math.max(0, (qs?.duration_minutes || 75) * 60 - elapsed);
  const timeUp = remaining <= 0;

  const [autoTried, setAutoTried] = useState(false);
  const inFlight = useRef(false);
  const submit = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    const body = { session_id: qs.session_id, answers, writing, duration_seconds: elapsed };
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data } = await api.post("/test/submit", body, { timeout: 30000 });
        localStorage.removeItem(PROGRESS_KEY);
        toast.success("Test submitted!");
        nav(`/results/${data.attempt_id}`, { replace: true });
        return;
      } catch (e) {
        const status = e.response?.status;
        const retryable = !status || status >= 500;
        if (retryable && attempt < 2) {
          await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
          continue;
        }
        setSubmitting(false);
        inFlight.current = false;
        if (status === 409) {
          localStorage.removeItem(PROGRESS_KEY);
          toast.success("Your test was already submitted — find it in your history.");
          nav("/dashboard", { replace: true });
        } else if (status === 401) {
          toast.error("Your login expired. Your answers are saved — please log in again and reopen the test.");
        } else if (status === 404) {
          localStorage.removeItem(PROGRESS_KEY);
          toast.error("This test session has expired. Please start a new test.");
        } else {
          const detail = formatApiErrorDetail(e.response?.data?.detail);
          toast.error(`Failed to submit${status ? ` (${status})` : " — check your connection"}. ${status ? detail : "Your answers are saved; please try again."}`);
        }
        return;
      }
    }
  }, [answers, writing, elapsed, nav, qs]);

  useEffect(() => {
    if (timeUp && started && !submitting && !autoTried) {
      setAutoTried(true);
      submit();
    }
  }, [timeUp, started, submit, submitting, autoTried]);

  if (!qs) {
    return (
      <div className="min-h-screen">
        <TopBar />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          {blocked ? (
            <div data-testid="test-limit-reached">
              <h1 className="text-3xl font-bold">No test attempts left</h1>
              <p className="mt-4 text-foreground/80">{blocked}</p>
              <Link to="/dashboard" data-testid="test-limit-dashboard-link" className="neu-btn-primary mt-8 inline-flex px-6 py-3 font-medium">See my results</Link>
            </div>
          ) : (
            <div className="font-mono-fig text-sm text-muted-foreground">Loading test…</div>
          )}
        </div>
      </div>
    );
  }

  if (!started) {
    return <Intro qs={qs} onStart={() => { startRef.current = Date.now(); setStarted(true); }} />;
  }

  const setAnswer = (qid, choice) => setAnswers((p) => {
    const next = { ...p, [qid]: choice };
    if (typeof choice === "string" && !choice.trim()) delete next[qid];
    return next;
  });
  const setW = (k) => (v) => setWriting((w) => ({ ...w, [k]: v }));
  const onPlayed = (key) => setPlays((p) => ({ ...p, [key]: (p[key] || 0) + 1 }));
  const goNext = () => setIdx((i) => Math.min(i + 1, steps.length - 1));
  const goPrev = () => setIdx((i) => Math.max(0, i - 1));
  const isLast = idx === steps.length - 1;
  const sectionOrder = [...new Set(steps.map((s) => s.section))];
  const sectionInfo = { ...SECTION_INFO[current.section], order: sectionOrder.indexOf(current.section) + 1, of: sectionOrder.length };

  return (
    <div className="min-h-screen relative pb-40">
      {/* Progress bar */}
      <div className="fixed top-0 inset-x-0 h-[3px] z-50 bg-foreground/10">
        <div className="h-full">
          <div
            className="h-full bg-signal transition-[width] duration-300"
            style={{ width: `${((idx + 1) / steps.length) * 100}%` }}
            data-testid="test-progress-bar"
          />
        </div>
      </div>

      <TimerFloating remaining={remaining} />

      <div className="max-w-5xl mx-auto px-6 pt-14 pb-8">
        <div className="flex items-end justify-between gap-4 mb-8 pb-6 rule-b">
          <div className="flex items-end gap-5">
            <div className="numeral text-6xl sm:text-7xl text-foreground" aria-hidden="true">0{sectionInfo.order}</div>
            <div>
            <div className="overline" data-testid="test-section-label">
              <span className="hidden sm:inline">Section {sectionInfo.order} / {sectionInfo.of} — </span>{sectionInfo.label}
            </div>
            <div className="mt-2 font-mono-fig text-sm text-muted-foreground">
              Page {idx + 1} <span className="opacity-50">/</span> {steps.length}
            </div>
            </div>
          </div>
          <div className="hidden sm:block neu-sm px-3 py-2 text-xs text-muted-foreground font-mono-fig">
            <span data-testid="test-answered-count">{answered}</span>
            <span className="opacity-50"> / </span>
            {totalMcq} answered
          </div>
        </div>

        <div key={idx} className="animate-fade-up">
          {current.kind === "mcq" && <McqQuestion step={current} value={answers[current.q.id]} onChange={(v) => setAnswer(current.q.id, v)} />}
          {current.kind === "part" && <PartStep step={current} answers={answers} setAnswer={setAnswer} plays={plays} onPlayed={onPlayed} />}
          {current.kind === "listening-part" && <ListeningPartStep step={current} answers={answers} setAnswer={setAnswer} plays={plays} onPlayed={onPlayed} />}
          {current.kind === "speaking" && <SpeakingStep task={qs.speaking} sessionId={qs.session_id} result={speech} onResult={setSpeech} />}
          {current.kind === "speaking-question" && <SpeakingStep kind="question" task={qs.speaking_question} sessionId={qs.session_id} result={speechQ} onResult={setSpeechQ} />}
          {current.kind === "writing-email" && <EmailTask task={qs.writing.email} value={writing.email} onChange={setW("email")} />}
          {current.kind === "writing-choice" && (
            <ChoiceTask choices={qs.writing.choices} choice={writing.choice} onChoice={setW("choice")} value={writing.extended} onChange={setW("extended")} />
          )}
        </div>

        <div className="mt-8 flex items-center justify-between gap-4">
          <button
            onClick={goPrev}
            disabled={idx === 0}
            data-testid="test-prev-btn"
            className="neu-btn inline-flex items-center gap-2 px-5 py-3 text-sm"
          >
            <ArrowLeft size={16} /> Previous
          </button>

          {isLast ? (
            <button
              onClick={() => setShowConfirm(true)}
              disabled={submitting}
              data-testid="test-submit-btn"
              className="neu-btn-primary inline-flex items-center gap-2 px-8 py-3.5"
            >
              {submitting ? "Submitting…" : "Submit test"}
              <ArrowRight size={16} weight="bold" />
            </button>
          ) : (
            <button
              onClick={goNext}
              data-testid="test-next-btn"
              className="neu-btn-primary inline-flex items-center gap-2 px-8 py-3.5"
            >
              Next <ArrowRight size={16} weight="bold" />
            </button>
          )}
        </div>
      </div>

      {showConfirm && (
        <ConfirmSubmit
          answered={answered}
          total={totalMcq}
          hasWriting={writing.email.trim().length > 0 && writing.extended.trim().length > 0}
          onCancel={() => setShowConfirm(false)}
          onConfirm={submit}
          submitting={submitting}
        />
      )}
    </div>
  );
}

function Intro({ qs, onStart }) {
  const [micOk, setMicOk] = useState(false);
  const locked = !!(qs.speaking || qs.speaking_question) && !micOk;
  const count = (parts) => parts.reduce((s, p) => s + p.questions.length, 0);
  const totalQs = qs.grammar.length + qs.vocabulary.length + count(qs.reading) + count(qs.listening) + 2;
  return (
    <div className="min-h-screen">
      <TopBar />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="neu rounded-3xl p-10 md:p-14">
          <div className="overline">Before you begin</div>
          <h1 className="text-4xl md:text-5xl mt-3 text-foreground font-bold tracking-tight leading-tight">
            A quick brief on the test.
          </h1>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Time limit", value: `${qs.duration_minutes}m` },
              { label: "Questions", value: `${totalQs}` },
              { label: "Skills", value: qs.speaking || qs.speaking_question ? "6" : "5" },
              { label: "Max score", value: `${qs.total_max}` },
            ].map((s) => (
              <div key={s.label} className="neu-inset rounded-2xl p-4 text-center">
                <div className="overline">{s.label}</div>
                <div className="font-mono-fig text-2xl text-foreground mt-2">{s.value}</div>
              </div>
            ))}
          </div>

          <ul className="mt-10 space-y-4 text-sm text-foreground/80 leading-relaxed">
            {[
              "You cannot pause the test once it starts. If time runs out, your test will be submitted automatically.",
              "Reading and listening follow B1 Preliminary-style formats: notices, matching, gapped texts, word gaps and note-taking.",
              "Each listening part is on one page: the recording at the top, all its questions below. You can play each recording up to 3 times. Make sure your speakers or headphones are on.",
              "Some gaps need a typed answer. Spelling counts, capital letters don't.",
              "Writing has 2 tasks: a required email answering 4 notes, then an article OR a story (about 100 words each).",
              ...(qs.speaking || qs.speaking_question ? [[
                qs.speaking && "describe a picture",
                qs.speaking_question && "answer a question about yourself",
              ].filter(Boolean).join(" and ")].map((what) => `Speaking: you'll ${what} out loud (about 1 minute each, up to 3 recordings each). Please allow microphone access.`) : []),
            ].map((t, i) => (
              <li key={i} className="flex gap-3">
                <span className="font-mono-fig font-bold text-foreground shrink-0">0{i + 1}</span>
                {t}
              </li>
            ))}
          </ul>

          {(qs.speaking || qs.speaking_question) && <MicCheck onPassed={() => setMicOk(true)} />}
          <button
            onClick={onStart}
            disabled={locked}
            data-testid="test-intro-start-btn"
            className="neu-btn-primary mt-12 inline-flex items-center gap-2 px-8 py-4 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Start the test <ArrowRight size={18} weight="bold" />
          </button>
          {locked && <p className="mt-3 text-xs text-muted-foreground" data-testid="test-intro-locked-hint">Pass the microphone check above to unlock the test.</p>}
        </div>
      </div>
    </div>
  );
}

function TimerFloating({ remaining }) {
  const m = Math.floor(remaining / 60);
  const s = remaining % 60;
  const low = remaining <= 300;
  return (
    <div className="fixed bottom-4 right-4 sm:bottom-auto sm:top-6 sm:right-6 z-40" data-testid="test-timer">
      <div className={`flex items-center gap-2 px-4 py-2 border ${low ? "bg-signal border-signal text-ink" : "bg-background border-foreground text-foreground"}`}>
        <ClockCountdown size={16} weight="bold" />
        <span className="font-mono-fig text-base font-bold tabular-nums">
          {String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
        </span>
      </div>
    </div>
  );
}

function McqQuestion({ step, value, onChange }) {
  return (
    <div>
      <div className="text-xs font-mono-fig text-muted-foreground">Difficulty · {step.q.difficulty}</div>
      <h2 className="text-3xl md:text-5xl mt-4 text-foreground font-bold leading-[1.05] tracking-tight max-w-4xl" data-testid="test-question-prompt">
        {step.q.prompt}
      </h2>
      <OptionList options={step.q.options} value={value} onChange={onChange} testIdPrefix="test-mcq" />
    </div>
  );
}

function ConfirmSubmit({ answered, total, hasWriting, onCancel, onConfirm, submitting }) {
  const missing = total - answered;
  return (
    <div className="fixed inset-0 bg-background/90 z-50 flex items-center justify-center p-6" data-testid="test-confirm-modal">
      <div className="neu-lg rounded-3xl max-w-md w-full p-8">
        <div className="flex items-center gap-3">
          <div className="neu-sm w-10 h-10 rounded-full flex items-center justify-center text-indigo">
            <WarningCircle size={20} weight="duotone" />
          </div>
          <div className="overline">Confirm submission</div>
        </div>
        <h3 className="text-2xl mt-5 text-foreground font-bold tracking-tight">Submit your test?</h3>
        <div className="mt-4 text-sm text-foreground/80 space-y-2">
          <div>You have answered <span className="font-mono-fig text-foreground">{answered} / {total}</span> questions.</div>
          {missing > 0 && (
            <div className="text-foreground font-semibold">
              {missing} question{missing === 1 ? " is" : "s are"} unanswered — they&apos;ll be marked as incorrect.
            </div>
          )}
          <div>Writing (both tasks): <span className="font-mono-fig text-foreground">{hasWriting ? "provided" : "incomplete"}</span></div>
        </div>
        <div className="mt-8 flex items-center gap-3 justify-end">
          <button
            onClick={onCancel}
            data-testid="test-confirm-cancel-btn"
            className="neu-btn px-5 py-2.5 text-sm"
          >
            Keep working
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            data-testid="test-confirm-submit-btn"
            className="neu-btn-primary px-6 py-3 text-sm"
          >
            {submitting ? "Submitting…" : "Submit for grading"}
          </button>
        </div>
      </div>
    </div>
  );
}
